import React, { useState, useCallback, useRef } from 'react';
import {
  Upload, FileText, FileSpreadsheet, Loader2, CheckCircle, AlertTriangle,
  X, ChevronDown, ChevronRight, Edit2, Trash2, Plus, Sparkles,
  Layers, Target, File, RefreshCw, Download, Eye, Save
} from 'lucide-react';
import { api } from '../api/client';
import { toast } from 'sonner';
import { WidgetErrorBoundary } from './ErrorBoundary';

// ==========================================
// CONSTANTS
// ==========================================

const FILE_ICONS = {
  pdf: FileText,
  docx: FileText,
  doc: FileText,
  txt: FileText,
  csv: FileSpreadsheet,
  xlsx: FileSpreadsheet,
  xls: FileSpreadsheet,
};

const HIERARCHY_COLORS = {
  theme: 'bg-purple-100 text-purple-700 border-purple-200',
  epic: 'bg-blue-100 text-blue-700 border-blue-200',
  requirement: 'bg-green-100 text-green-700 border-green-200',
};

const HIERARCHY_ICONS = {
  theme: Layers,
  epic: Target,
  requirement: FileText,
};

// ==========================================
// EXTRACTED ITEM COMPONENT
// ==========================================

const ExtractedItemRow = ({ 
  item, 
  index, 
  isExpanded, 
  onToggle, 
  onEdit, 
  onDelete, 
  depth = 0 
}) => {
  const Icon = HIERARCHY_ICONS[item.hierarchy_type] || FileText;
  const typeColor = HIERARCHY_COLORS[item.hierarchy_type] || 'bg-gray-100 text-gray-700';
  
  const hasDetails = item.acceptance_criteria?.length > 0 || 
                     item.risks?.length > 0 || 
                     item.constraints?.length > 0;

  return (
    <div className="border-b border-gray-100 last:border-0">
      <div 
        style={{ paddingLeft: `${depth * 24 + 12}px` }}
        className="flex items-center gap-3 py-3 px-3 hover:bg-gray-50 transition-colors group"
      >
        {/* Expand/collapse for items with details */}
        {hasDetails ? (
          <button 
            onClick={() => onToggle(index)}
            className="p-0.5 hover:bg-gray-200 rounded"
          >
            {isExpanded ? (
              <ChevronDown className="w-4 h-4 text-gray-500" />
            ) : (
              <ChevronRight className="w-4 h-4 text-gray-500" />
            )}
          </button>
        ) : (
          <span className="w-5" />
        )}

        {/* Type badge */}
        <span className={`flex items-center gap-1 px-2 py-0.5 rounded border text-xs font-medium ${typeColor}`}>
          <Icon className="w-3 h-3" />
          {item.hierarchy_type}
        </span>

        {/* Name */}
        <span className="flex-1 text-sm font-medium text-gray-800 truncate">
          {item.name}
        </span>

        {/* Category badge */}
        {item.category && (
          <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded">
            {item.category}
          </span>
        )}

        {/* Confidence indicator */}
        <span 
          className={`text-xs px-1.5 py-0.5 rounded ${
            item.confidence >= 0.8 ? 'bg-green-100 text-green-700' :
            item.confidence >= 0.6 ? 'bg-amber-100 text-amber-700' :
            'bg-red-100 text-red-700'
          }`}
          title={`AI Confidence: ${Math.round(item.confidence * 100)}%`}
        >
          {Math.round(item.confidence * 100)}%
        </span>

        {/* Actions */}
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <button 
            onClick={() => onEdit(index)}
            className="p-1 hover:bg-gray-200 rounded"
            title="Edit"
          >
            <Edit2 className="w-3.5 h-3.5 text-gray-500" />
          </button>
          <button 
            onClick={() => onDelete(index)}
            className="p-1 hover:bg-red-100 rounded"
            title="Delete"
          >
            <Trash2 className="w-3.5 h-3.5 text-red-500" />
          </button>
        </div>
      </div>

      {/* Expanded details */}
      {isExpanded && hasDetails && (
        <div 
          style={{ paddingLeft: `${depth * 24 + 48}px` }}
          className="pb-3 pr-3 text-xs space-y-2"
        >
          {item.description && (
            <p className="text-gray-600 italic">{item.description}</p>
          )}
          
          {item.acceptance_criteria?.length > 0 && (
            <div className="p-2 bg-green-50 rounded">
              <p className="font-medium text-green-700 mb-1">
                Acceptance Criteria ({item.acceptance_criteria.length})
              </p>
              {item.acceptance_criteria.map((ac, i) => (
                <div key={i} className="text-green-600 ml-2">
                  {ac.type === 'gherkin' ? (
                    <span>Given {ac.given}, When {ac.when}, Then {ac.then}</span>
                  ) : (
                    <span>{ac.content}</span>
                  )}
                </div>
              ))}
            </div>
          )}

          {item.risks?.length > 0 && (
            <div className="p-2 bg-red-50 rounded">
              <p className="font-medium text-red-700 mb-1">
                Risks ({item.risks.length})
              </p>
              {item.risks.map((risk, i) => (
                <div key={i} className="text-red-600 ml-2">
                  [{risk.category}] {risk.description} - L:{risk.likelihood} I:{risk.impact}
                </div>
              ))}
            </div>
          )}

          {item.constraints?.length > 0 && (
            <div className="p-2 bg-blue-50 rounded">
              <p className="font-medium text-blue-700 mb-1">
                Constraints ({item.constraints.length})
              </p>
              {item.constraints.map((c, i) => (
                <div key={i} className="text-blue-600 ml-2">
                  [{c.category}] {c.description}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ==========================================
// EDIT ITEM MODAL
// ==========================================

const EditItemModal = ({ item, index, onSave, onClose }) => {
  const [formData, setFormData] = useState({
    name: item?.name || '',
    description: item?.description || '',
    hierarchy_type: item?.hierarchy_type || 'requirement',
    category: item?.category || 'Functional',
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(index, { ...item, ...formData });
  };

  if (!item) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg m-4">
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="font-semibold text-gray-900">Edit Item</h3>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--clr-brand-primary)] focus:outline-none"
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--clr-brand-primary)] focus:outline-none"
            />
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
              <select
                value={formData.hierarchy_type}
                onChange={(e) => setFormData({ ...formData, hierarchy_type: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--clr-brand-primary)] focus:outline-none"
              >
                <option value="theme">Theme</option>
                <option value="epic">Epic</option>
                <option value="requirement">Requirement</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--clr-brand-primary)] focus:outline-none"
              >
                <option value="Functional">Functional</option>
                <option value="Non-Functional">Non-Functional</option>
                <option value="Integration">Integration</option>
                <option value="Compliance">Compliance</option>
                <option value="UX">UX</option>
                <option value="Technical">Technical</option>
              </select>
            </div>
          </div>
          
          <div className="flex justify-end gap-2 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-[var(--clr-brand-primary)] text-white rounded-lg hover:opacity-90"
            >
              Save Changes
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ==========================================
// MAIN SPEC UPLOAD MODAL
// ==========================================

const SpecUploadModal = ({
  isOpen,
  onClose,
  tenantId,
  projectId,
  userId,
  onImportComplete,
}) => {
  const fileInputRef = useRef(null);
  const [step, setStep] = useState('upload'); // upload, processing, preview, importing
  const [file, setFile] = useState(null);
  const [extractionResult, setExtractionResult] = useState(null);
  const [items, setItems] = useState([]);
  const [expandedIndices, setExpandedIndices] = useState(new Set());
  const [editingIndex, setEditingIndex] = useState(null);
  const [error, setError] = useState(null);

  const resetState = () => {
    setStep('upload');
    setFile(null);
    setExtractionResult(null);
    setItems([]);
    setExpandedIndices(new Set());
    setEditingIndex(null);
    setError(null);
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
      setError(null);
    }
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    const droppedFile = e.dataTransfer.files?.[0];
    if (droppedFile) {
      setFile(droppedFile);
      setError(null);
    }
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
  }, []);

  const handleUpload = async () => {
    if (!file) return;
    
    setStep('processing');
    setError(null);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('tenant_id', tenantId);
      if (projectId) {
        formData.append('project_id', projectId);
      }
      formData.append('extraction_mode', 'full');
      
      const response = await api.uploadSpec(formData);
      
      setExtractionResult(response.data);
      setItems(response.data.items || []);
      setStep('preview');
      toast.success(`Extracted ${response.data.items?.length || 0} items`);
    } catch (err) {
      console.error('Upload failed:', err);
      
      // Provide more specific error messages
      let errorMsg = 'Failed to process file';
      if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
        errorMsg = 'Processing timed out. Please try with a smaller file (under 50 pages recommended).';
      } else if (err.response?.data?.detail) {
        errorMsg = err.response.data.detail;
      } else if (err.response?.status === 413) {
        errorMsg = 'File too large. Maximum size is 20MB.';
      } else if (err.response?.status >= 500) {
        errorMsg = 'Server error during AI processing. Please try again or contact support.';
      }
      
      setError(errorMsg);
      setStep('upload');
      toast.error(errorMsg);
    }
  };

  const handleToggleExpand = (index) => {
    setExpandedIndices(prev => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  const handleEditItem = (index) => {
    setEditingIndex(index);
  };

  const handleSaveEdit = (index, updatedItem) => {
    setItems(prev => {
      const newItems = [...prev];
      newItems[index] = updatedItem;
      return newItems;
    });
    setEditingIndex(null);
  };

  const handleDeleteItem = (index) => {
    setItems(prev => prev.filter((_, i) => i !== index));
    toast.success('Item removed');
  };

  const handleImport = async () => {
    if (!items.length) return;
    
    // If no projectId, skip database import and just return items to parent
    // This is used in the Scoping Wizard where project doesn't exist yet
    if (!projectId) {
      toast.success(`Added ${items.length} items to session`);
      onImportComplete?.({ items: items, imported_count: items.length });
      handleClose();
      return;
    }
    
    setStep('importing');
    
    try {
      const response = await api.importExtractedRequirements({
        tenant_id: tenantId,
        project_id: projectId,
        items: items,
        user_id: userId,
      });
      
      toast.success(`Imported ${response.data.imported_count} items`);
      onImportComplete?.(response.data);
      handleClose();
    } catch (err) {
      console.error('Import failed:', err);
      setError(err.response?.data?.detail || 'Failed to import requirements');
      setStep('preview');
      toast.error('Failed to import requirements');
    }
  };

  if (!isOpen) return null;

  const FileIcon = file ? FILE_ICONS[file.name.split('.').pop().toLowerCase()] || File : File;

  return (
    <WidgetErrorBoundary widgetName="Spec Upload">
      <>
        {/* Overlay */}
        <div className="fixed inset-0 bg-black/50 z-40" onClick={handleClose} />
        
        {/* Modal */}
        <div className="fixed inset-4 md:inset-10 bg-white rounded-2xl shadow-2xl z-50 flex flex-col overflow-hidden">
          {/* Header */}
          <div className="px-6 py-4 border-b bg-gradient-to-r from-purple-600 to-indigo-600">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3 text-white">
                <div className="p-2 bg-white/20 rounded-lg">
                  <Sparkles className="w-5 h-5" />
                </div>
                <div>
                  <h2 className="font-semibold text-lg">Upload Requirements Spec</h2>
                  <p className="text-xs text-white/80">
                    AI-powered extraction from PDF, Word, Excel, or Text files
                  </p>
                </div>
              </div>
              <button
                onClick={handleClose}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-white" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {/* Step: Upload */}
            {step === 'upload' && (
              <div className="max-w-xl mx-auto">
                <div
                  onDrop={handleDrop}
                  onDragOver={handleDragOver}
                  onClick={() => fileInputRef.current?.click()}
                  className={`border-2 border-dashed rounded-xl p-12 text-center cursor-pointer transition-colors ${
                    file ? 'border-green-400 bg-green-50' : 'border-gray-300 hover:border-purple-400 hover:bg-purple-50'
                  }`}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf,.docx,.doc,.txt,.csv,.xlsx,.xls"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                  
                  {file ? (
                    <div className="space-y-4">
                      <FileIcon className="w-16 h-16 text-green-500 mx-auto" />
                      <div>
                        <p className="font-medium text-gray-900">{file.name}</p>
                        <p className="text-sm text-gray-500">
                          {(file.size / 1024).toFixed(1)} KB
                        </p>
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setFile(null);
                        }}
                        className="text-sm text-red-600 hover:text-red-700"
                      >
                        Remove file
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <Upload className="w-16 h-16 text-gray-400 mx-auto" />
                      <div>
                        <p className="font-medium text-gray-700">
                          Drop your file here or click to browse
                        </p>
                        <p className="text-sm text-gray-500 mt-1">
                          Supports PDF, DOCX, TXT, CSV, XLSX (max 20MB)
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                {error && (
                  <div className="mt-4 p-4 bg-red-50 rounded-lg border border-red-200">
                    <div className="flex items-center gap-2 text-red-600">
                      <AlertTriangle className="w-5 h-5" />
                      <span className="font-medium">Error</span>
                    </div>
                    <p className="text-sm text-red-700 mt-1">{error}</p>
                  </div>
                )}

                <button
                  onClick={handleUpload}
                  disabled={!file}
                  className="w-full mt-6 py-3 bg-[var(--clr-brand-primary)] text-white rounded-lg font-medium hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  <Sparkles className="w-5 h-5" />
                  Extract Requirements with AI
                </button>
              </div>
            )}

            {/* Step: Processing */}
            {step === 'processing' && (
              <div className="max-w-md mx-auto text-center py-12">
                <Loader2 className="w-16 h-16 text-purple-600 animate-spin mx-auto mb-6" />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  Extracting Requirements...
                </h3>
                <p className="text-gray-500">
                  AI is analyzing your document and extracting themes, epics, requirements,
                  acceptance criteria, risks, and constraints.
                </p>
                <div className="mt-6 space-y-2 text-sm text-gray-400">
                  <p>• Parsing document structure</p>
                  <p>• Identifying requirement patterns</p>
                  <p>• Generating acceptance criteria</p>
                  <p>• Detecting risks and constraints</p>
                </div>
              </div>
            )}

            {/* Step: Preview */}
            {step === 'preview' && extractionResult && (
              <div className="space-y-6">
                {/* Summary Cards */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  <div className="bg-purple-50 rounded-lg p-4 text-center">
                    <p className="text-2xl font-bold text-purple-700">
                      {extractionResult.summary?.total_themes || 0}
                    </p>
                    <p className="text-xs text-purple-600">Themes</p>
                  </div>
                  <div className="bg-blue-50 rounded-lg p-4 text-center">
                    <p className="text-2xl font-bold text-blue-700">
                      {extractionResult.summary?.total_epics || 0}
                    </p>
                    <p className="text-xs text-blue-600">Epics</p>
                  </div>
                  <div className="bg-green-50 rounded-lg p-4 text-center">
                    <p className="text-2xl font-bold text-green-700">
                      {extractionResult.summary?.total_requirements || 0}
                    </p>
                    <p className="text-xs text-green-600">Requirements</p>
                  </div>
                  <div className="bg-amber-50 rounded-lg p-4 text-center">
                    <p className="text-2xl font-bold text-amber-700">
                      {extractionResult.summary?.total_ac || 0}
                    </p>
                    <p className="text-xs text-amber-600">Acceptance Criteria</p>
                  </div>
                  <div className="bg-red-50 rounded-lg p-4 text-center">
                    <p className="text-2xl font-bold text-red-700">
                      {extractionResult.summary?.total_risks || 0}
                    </p>
                    <p className="text-xs text-red-600">Risks</p>
                  </div>
                </div>

                {/* Extraction info */}
                <div className="flex items-center justify-between text-sm text-gray-500">
                  <span>
                    Extracted from: <strong>{extractionResult.file_name}</strong>
                    {extractionResult.page_count > 0 && ` (${extractionResult.page_count} pages)`}
                  </span>
                  <span>
                    {extractionResult.word_count.toLocaleString()} words • 
                    {(extractionResult.extraction_time_ms / 1000).toFixed(1)}s
                  </span>
                </div>

                {/* Items list */}
                <div className="border rounded-lg overflow-hidden">
                  <div className="bg-gray-50 px-4 py-3 border-b flex items-center justify-between">
                    <h4 className="font-medium text-gray-700">
                      Extracted Items ({items.length})
                    </h4>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setExpandedIndices(new Set(items.map((_, i) => i)))}
                        className="text-xs text-gray-500 hover:text-gray-700"
                      >
                        Expand All
                      </button>
                      <span className="text-gray-300">|</span>
                      <button
                        onClick={() => setExpandedIndices(new Set())}
                        className="text-xs text-gray-500 hover:text-gray-700"
                      >
                        Collapse All
                      </button>
                    </div>
                  </div>
                  
                  <div className="max-h-[400px] overflow-y-auto">
                    {items.map((item, index) => (
                      <ExtractedItemRow
                        key={index}
                        item={item}
                        index={index}
                        isExpanded={expandedIndices.has(index)}
                        onToggle={handleToggleExpand}
                        onEdit={handleEditItem}
                        onDelete={handleDeleteItem}
                        depth={item.hierarchy_type === 'theme' ? 0 : item.hierarchy_type === 'epic' ? 1 : 2}
                      />
                    ))}
                  </div>
                </div>

                {/* Info note */}
                <div className="bg-blue-50 rounded-lg p-4 flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-blue-500 mt-0.5 shrink-0" />
                  <div className="text-sm text-blue-700">
                    <p className="font-medium">Review before importing</p>
                    <p className="text-blue-600 mt-1">
                      The AI has extracted items from your document. Review and edit any items 
                      as needed before importing them into your project. You can delete items 
                      that are not relevant.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Step: Importing */}
            {step === 'importing' && (
              <div className="max-w-md mx-auto text-center py-12">
                <Loader2 className="w-16 h-16 text-green-600 animate-spin mx-auto mb-6" />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  Importing Requirements...
                </h3>
                <p className="text-gray-500">
                  Creating {items.length} items in your project...
                </p>
              </div>
            )}
          </div>

          {/* Footer */}
          {step === 'preview' && (
            <div className="px-6 py-4 border-t bg-gray-50 flex items-center justify-between">
              <button
                onClick={resetState}
                className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:bg-white rounded-lg"
              >
                <RefreshCw className="w-4 h-4" />
                Start Over
              </button>
              <div className="flex items-center gap-3">
                <button
                  onClick={handleClose}
                  className="px-4 py-2 text-gray-700 hover:bg-white rounded-lg"
                >
                  Cancel
                </button>
                <button
                  onClick={handleImport}
                  disabled={items.length === 0}
                  className="flex items-center gap-2 px-6 py-2 bg-[var(--clr-brand-primary)] text-white rounded-lg hover:opacity-90 disabled:opacity-50"
                >
                  <Save className="w-4 h-4" />
                  Import {items.length} Items
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Edit Modal */}
        {editingIndex !== null && (
          <EditItemModal
            item={items[editingIndex]}
            index={editingIndex}
            onSave={handleSaveEdit}
            onClose={() => setEditingIndex(null)}
          />
        )}
      </>
    </WidgetErrorBoundary>
  );
};

export default SpecUploadModal;
